<template>
  <div class="secondaryFont form">
    <h3>Datos generales del acta</h3>
    <div class="gridInputs">
      <div
        class="fieldBlock"
        v-for="field in datosGeneralesFields"
        :key="`datos-generales-${field.key}`"
      >
        <label class="labelInput">
          {{ field.label }} <span v-if="field.required" class="required">*</span>
        </label>

        <Dropdown
          v-if="field.type === 'dropdown'"
          v-model="sifcopStore.form.datosGeneralesActa[field.key]"
          :options="yesNoOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="field.placeholder"
          class="mt-2 input"
          :disabled="sifcopStore.isView"
          @focus="markTouched('datosGeneralesActa', field.key)"
          @change="markTouched('datosGeneralesActa', field.key)"
          @blur="markTouched('datosGeneralesActa', field.key)"
        />

        <Dropdown
          v-else-if="field.type === 'monthSelect'"
          v-model="sifcopStore.form.datosGeneralesActa[field.key]"
          :options="monthOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="field.placeholder"
          class="mt-2 input"
          :disabled="sifcopStore.isView"
          @focus="markTouched('datosGeneralesActa', field.key)"
          @change="markTouched('datosGeneralesActa', field.key)"
          @blur="markTouched('datosGeneralesActa', field.key)"
        />

        <CustomCalendar
          v-else-if="field.type === 'date'"
          class="mt-2 calendarWrapper"
          inputClass="input nativeInput"
          :inputStyle="{ width: '100%' }"
          :selectOtherMonths="true"
          onkeydown="return false;"
          placeholder="Seleccione fecha"
          dateFormat="dd/mm/yy"
          v-model="sifcopStore.form.datosGeneralesActa[field.key]"
          :maxDate="today"
          :baseZIndex="999999"
          :disabled="sifcopStore.isView"
          @focus="markTouched('datosGeneralesActa', field.key)"
          @input="markTouched('datosGeneralesActa', field.key)"
          @blur="markTouched('datosGeneralesActa', field.key)"
        />

        <CustomCalendar
          v-else-if="field.type === 'time'"
          class="mt-2 calendarWrapper"
          inputClass="input nativeInput"
          :inputStyle="{ width: '100%' }"
          v-model="horaCalendarValue"
          :manualInput="false"
          :timeOnly="true"
          hourFormat="24"
          placeholder="Seleccione hora"
          :baseZIndex="999999"
          :disabled="sifcopStore.isView"
          @focus="markTouched('datosGeneralesActa', field.key)"
          @input="markTouched('datosGeneralesActa', field.key)"
          @blur="markTouched('datosGeneralesActa', field.key)"
        />

        <InputText
          v-else
          v-no-emoji
          class="mt-2 input"
          type="text"
          :placeholder="field.placeholder"
          :maxlength="field.maxLength"
          :inputmode="field.inputMode || 'text'"
          v-model="sifcopStore.form.datosGeneralesActa[field.key]"
          :disabled="sifcopStore.isView"
          @focus="markTouched('datosGeneralesActa', field.key)"
          @input="
            sanitizeField('datosGeneralesActa', field.key);
            markTouched('datosGeneralesActa', field.key);
          "
          @blur="markTouched('datosGeneralesActa', field.key)"
        />

        <small
          class="errorText"
          v-if="
            shouldShowError(
              'datosGeneralesActa',
              field.key,
              sifcopStore.form.datosGeneralesActa[field.key]
            )
          "
        >
          {{ errorFor(buildFieldPath("datosGeneralesActa", field.key)) }}
        </small>
      </div>
    </div>

    <h3>Datos de la persona registrada en SIFCOP</h3>
    <div class="gridInputs">
      <div class="fieldBlock" v-for="field in personaFields" :key="`persona-${field.key}`">
        <label class="labelInput">
          {{ field.label }} <span v-if="field.required" class="required">*</span>
        </label>

        <InputMask
          v-if="field.type === 'birthDate'"
          v-no-emoji
          class="mt-2 input nativeInput"
          mask="99/99/9999"
          v-model="sifcopStore.form.personaRegistrada[field.key]"
          placeholder="dd/mm/yyyy"
          slotChar="dd/mm/yyyy"
          :disabled="sifcopStore.isView"
          @focus="markTouched('personaRegistrada', field.key)"
          @input="markTouched('personaRegistrada', field.key)"
          @blur="markTouched('personaRegistrada', field.key)"
        />

        <textarea
          v-else-if="field.type === 'textarea'"
          v-no-emoji
          class="mt-2 input textareaInput secondaryFont"
          :placeholder="field.placeholder"
          :maxlength="field.maxLength"
          v-model="sifcopStore.form.personaRegistrada[field.key]"
          :disabled="sifcopStore.isView"
          @focus="markTouched('personaRegistrada', field.key)"
          @input="
            sanitizeField('personaRegistrada', field.key);
            markTouched('personaRegistrada', field.key);
          "
          @blur="markTouched('personaRegistrada', field.key)"
        />

        <InputText
          v-else
          v-no-emoji
          class="mt-2 input"
          type="text"
          :placeholder="field.placeholder"
          :maxlength="field.maxLength"
          :inputmode="field.inputMode || 'text'"
          v-model="sifcopStore.form.personaRegistrada[field.key]"
          :disabled="sifcopStore.isView"
          @focus="markTouched('personaRegistrada', field.key)"
          @input="
            sanitizeField('personaRegistrada', field.key);
            markTouched('personaRegistrada', field.key);
          "
          @blur="markTouched('personaRegistrada', field.key)"
        />

        <small
          class="errorText"
          v-if="
            shouldShowError(
              'personaRegistrada',
              field.key,
              sifcopStore.form.personaRegistrada[field.key]
            )
          "
        >
          {{ errorFor(buildFieldPath("personaRegistrada", field.key)) }}
        </small>
      </div>
    </div>

    <h3>Datos de vehiculos (A) / Armas (B) / Elementos (C) registrado en el SIFCOP</h3>

    <div class="subSection" v-for="section in dynamicSectionsWithActions" :key="section.key">
      <div class="subSectionHeader">
        <h4>{{ section.title }}</h4>
        <Button
          v-if="!sifcopStore.isView"
          class="p-button-sm p-button-outlined addButton"
          icon="pi pi-plus"
          :label="section.addLabel"
          @click="section.add()"
        />
      </div>

      <div
        class="dynamicCard"
        v-for="(item, index) in sifcopStore.form[section.key]"
        :key="`${section.key}-${index}`"
      >
        <div class="dynamicCardHeader">
          <span>{{ section.itemLabel }} {{ index + 1 }}</span>
          <Button
            v-if="!sifcopStore.isView"
            class="p-button-rounded p-button-danger p-button-text p-button-sm"
            icon="pi pi-trash"
            @click="handleRemoveSectionItem(section.key, index)"
          />
        </div>

        <div class="gridInputs">
          <div
            class="fieldBlock"
            :class="{ textareaRow: field.type === 'textarea' }"
            v-for="field in section.fields"
            :key="field.key"
          >
            <label class="labelInput">{{ field.label }}</label>

            <InputText
              v-if="field.type !== 'textarea'"
              v-no-emoji
              class="mt-2 input"
              type="text"
              :placeholder="field.placeholder"
              :maxlength="field.maxLength"
              :inputmode="field.inputMode || 'text'"
              v-model="item[field.key]"
              :disabled="sifcopStore.isView"
              @focus="markTouched(section.key, field.key, index)"
              @input="
                sanitizeDynamicField(section.key, index, field.key);
                markTouched(section.key, field.key, index);
              "
              @blur="markTouched(section.key, field.key, index)"
            />

            <textarea
              v-else
              v-no-emoji
              class="mt-2 input textareaInput secondaryFont"
              :placeholder="field.placeholder"
              :maxlength="field.maxLength"
              v-model="item[field.key]"
              :disabled="sifcopStore.isView"
              @focus="markTouched(section.key, field.key, index)"
              @input="
                sanitizeDynamicField(section.key, index, field.key);
                markTouched(section.key, field.key, index);
              "
              @blur="markTouched(section.key, field.key, index)"
            />

            <small
              class="errorText"
              v-if="shouldShowError(section.key, field.key, item[field.key], index)"
            >
              {{ errorFor(buildFieldPath(section.key, field.key, index)) }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showPdfButton" class="actionsRow">
      <Button
        class="saveButton flex-grow-0"
        :disabled="isPdfButtonDisabled"
        @click="handleGeneratePdf"
      >
        <div>
          <FileText color="white" :size="20" class="flex mr-1" />
        </div>
        {{ isGeneratingPdf ? "Generando PDF..." : "Generar PDF" }}
      </Button>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import InputMask from "primevue/inputmask";
import { FileText } from "lucide-vue";
import CustomCalendar from "@/components/custom-calendar/CustomCalendar.vue";
import { useSifcopStore } from "@/store/Pinia/useSifcopStore";
import {
  datosGeneralesFields,
  personaFields,
  dynamicSections,
  monthOptions,
  sanitizeSifcopValue,
} from "@/utils/sifcopValidation";
import { generateSifcopPdf } from "@/utils/sifcopPdf";

export default {
  name: "SifcopForm",
  props: {
    showPdfButton: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    InputMask,
    CustomCalendar,
    FileText,
  },
  setup() {
    const sifcopStore = useSifcopStore();
    const isGeneratingPdf = ref(false);
    const today = computed(() => new Date());

    const isPdfButtonDisabled = computed(() => isGeneratingPdf.value || !sifcopStore.isFormValid);

    const yesNoOptions = [
      { label: "Sí", value: "SI" },
      { label: "No", value: "NO" },
    ];

    const touchedFields = ref({});

    const dynamicSectionsWithActions = computed(() =>
      dynamicSections.map((section) => ({
        ...section,
        add: () => {
          if (section.key === "vehiculos") sifcopStore.addVehiculo();
          if (section.key === "armas") sifcopStore.addArma();
          if (section.key === "elementos") sifcopStore.addElemento();
        },
      }))
    );

    const errorFor = (fieldPath) => sifcopStore.validationErrors?.[fieldPath] || "";

    const buildFieldPath = (sectionName, fieldKey, index = null) =>
      index === null ? `${sectionName}.${fieldKey}` : `${sectionName}.${index}.${fieldKey}`;

    const hasValue = (value) => {
      if (value instanceof Date) {
        return !Number.isNaN(value.getTime());
      }

      if (typeof value === "string") {
        return value.trim() !== "";
      }

      return value !== null && value !== undefined;
    };

    const markTouched = (sectionName, fieldKey, index = null) => {
      const fieldPath = buildFieldPath(sectionName, fieldKey, index);
      touchedFields.value = {
        ...touchedFields.value,
        [fieldPath]: true,
      };
    };

    const shouldShowError = (sectionName, fieldKey, value, index = null) => {
      const fieldPath = buildFieldPath(sectionName, fieldKey, index);
      return Boolean(errorFor(fieldPath)) && (touchedFields.value[fieldPath] || hasValue(value));
    };

    const resetTouchedFields = () => {
      touchedFields.value = {};
    };

    const clearTouchedSection = (sectionKey) => {
      touchedFields.value = Object.fromEntries(
        Object.entries(touchedFields.value).filter(
          ([fieldPath]) => !fieldPath.startsWith(`${sectionKey}.`)
        )
      );
    };

    const handleRemoveSectionItem = (sectionKey, index) => {
      clearTouchedSection(sectionKey);

      if (sectionKey === "vehiculos") sifcopStore.removeVehiculo(index);
      if (sectionKey === "armas") sifcopStore.removeArma(index);
      if (sectionKey === "elementos") sifcopStore.removeElemento(index);
    };

    const parseHoraToDate = (hora) => {
      if (typeof hora !== "string" || !/^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.test(hora)) {
        return null;
      }

      const [hours, minutes] = hora.split(":").map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    };

    const formatHoraFromDate = (value) => {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";

      const hours = `${value.getHours()}`.padStart(2, "0");
      const minutes = `${value.getMinutes()}`.padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    const horaCalendarValue = computed({
      get: () => parseHoraToDate(sifcopStore.form.datosGeneralesActa.hora),
      set: (value) => {
        sifcopStore.form.datosGeneralesActa.hora = formatHoraFromDate(value);
      },
    });

    const updateField = (sectionName, fieldKey, rawValue) => {
      const sanitizedValue = sanitizeSifcopValue(sectionName, fieldKey, rawValue);
      sifcopStore.form[sectionName][fieldKey] = sanitizedValue;
    };

    const sanitizeField = (sectionName, fieldKey) => {
      const rawValue = sifcopStore.form[sectionName][fieldKey];
      updateField(sectionName, fieldKey, rawValue);
    };

    const updateDynamicField = (sectionKey, index, fieldKey, rawValue) => {
      const sanitizedValue = sanitizeSifcopValue(sectionKey, fieldKey, rawValue);
      sifcopStore.form[sectionKey][index][fieldKey] = sanitizedValue;
    };

    const sanitizeDynamicField = (sectionKey, index, fieldKey) => {
      const rawValue = sifcopStore.form[sectionKey][index][fieldKey];
      updateDynamicField(sectionKey, index, fieldKey, rawValue);
    };

    const handleGeneratePdf = async () => {
      if (isPdfButtonDisabled.value) return;

      isGeneratingPdf.value = true;

      try {
        await generateSifcopPdf({
          formData: sifcopStore.form,
          outputFileName: `sifcop-${Date.now()}.pdf`,
        });
      } catch (error) {
        console.error("Error al generar PDF de SIFCOP:", error);
      } finally {
        isGeneratingPdf.value = false;
      }
    };

    const syncDatePartsFromFecha = (fecha) => {
      if (!fecha) {
        sifcopStore.form.datosGeneralesActa.dia = "";
        sifcopStore.form.datosGeneralesActa.mes = "";
        sifcopStore.form.datosGeneralesActa.anio = "";
        return;
      }

      const parsedDate = fecha instanceof Date ? fecha : new Date(fecha);
      if (Number.isNaN(parsedDate.getTime())) {
        sifcopStore.form.datosGeneralesActa.dia = "";
        sifcopStore.form.datosGeneralesActa.mes = "";
        sifcopStore.form.datosGeneralesActa.anio = "";
        return;
      }

      const anio = `${parsedDate.getFullYear()}`;
      const mes = parsedDate.getMonth();
      const dia = `${parsedDate.getDate()}`;
      const monthOption = monthOptions[mes];

      sifcopStore.form.datosGeneralesActa.dia = dia;
      sifcopStore.form.datosGeneralesActa.mes = monthOption?.value || "";
      sifcopStore.form.datosGeneralesActa.anio = anio || "";
    };

    onMounted(() => {
      if (sifcopStore.isView) {
        sifcopStore.startEdit();
      }

      if (sifcopStore.form.datosGeneralesActa.fecha) {
        syncDatePartsFromFecha(sifcopStore.form.datosGeneralesActa.fecha);
      }

      sifcopStore.validateForm();
      resetTouchedFields();
    });

    watch(
      () => sifcopStore.formType,
      () => {
        resetTouchedFields();
      }
    );

    watch(
      () => sifcopStore.form.datosGeneralesActa.fecha,
      (newFecha, oldFecha) => {
        if (newFecha !== oldFecha) {
          syncDatePartsFromFecha(newFecha);
        }
      }
    );

    watch(
      () => sifcopStore.form,
      () => {
        sifcopStore.validateForm();
      },
      { deep: true }
    );

    return {
      sifcopStore,
      today,
      datosGeneralesFields,
      personaFields,
      dynamicSectionsWithActions,
      monthOptions,
      yesNoOptions,
      errorFor,
      buildFieldPath,
      markTouched,
      shouldShowError,
      handleRemoveSectionItem,
      horaCalendarValue,
      isPdfButtonDisabled,
      isGeneratingPdf,
      sanitizeField,
      sanitizeDynamicField,
      handleGeneratePdf,
    };
  },
};
</script>

<style scoped>
.addButton {
  color: #0b5195;
  border-color: #0b5195;
}

.required {
  color: #d20f39;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 1.2em;
  background: #f7f6f680;
  padding: 0.8rem;
  border-radius: 8px;
}

.subSection {
  background-color: #eaeaea50;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #d9d9d9;
}

.subSectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.subSectionHeader h4 {
  margin: 0;
  color: #0b5195;
}

.dynamicCard {
  border: 1px solid #dadada;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}

.dynamicCard:last-child {
  margin-bottom: 0;
}

.dynamicCardHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #0b5195;
  font-weight: 600;
}

.gridInputs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;
}

.fieldBlock {
  display: flex;
  flex-direction: column;
}

.textareaRow {
  grid-column: 1 / -1;
}

.labelInput {
  color: #0b5195;
  font-size: 16px;
  font-weight: 400;
}

.input {
  border: 1px solid #0b5195;
  border-radius: 6px;
  width: 100%;
  color: #111827;
  background-color: #ffffff;
}

.calendarWrapper {
  display: block;
  width: 100%;
  border: 1px solid #0b5195;
  border-radius: 6px;
}

.nativeInput {
  min-height: 42px;
  padding: 0.65rem 0.75rem;
}

.textareaInput {
  min-height: 88px;
  padding: 0.75rem;
  resize: vertical;
  font-size: 1rem;
}

.input:focus {
  border: 1px solid #0b5195 !important;
  box-shadow: 0 0 0 0.1rem #0b519533 !important;
  outline: none;
}

.errorText {
  color: #d20f39;
  margin-top: 0.2rem;
}

.actionsRow {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.saveButton {
  background-color: #0b5195;
  border: none;
  border-radius: 6px;
  min-width: 130px;
}

.saveButton:hover {
  background-color: #1366b0;
}

#app
  > div:nth-child(2)
  > div
  > div.content
  > div.sifcop-container
  > div.secondaryFont.form
  > div:nth-child(2)
  > div:nth-child(4)
  > span {
  padding: 0;
  border: 1px solid #0b5195;
  border-radius: 6px;
}

@media (max-width: 1200px) {
  .gridInputs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .gridInputs {
    grid-template-columns: 1fr;
  }
}
</style>
